import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {
  decrementTotalData,
  getScenarios,
  setInactiveData,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalData,
  setTotalPages,
} from "@redux/slices/scenarioSlices/tableSlice";
import {
  archiveScenario,
  deleteScenario,
  unArchiveScenario,
} from "@redux/slices/scenarioSlices/viewSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const ScenarioTablePage = () => {
  const {openPopup, closePopup} = usePopup();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo} = useSelector((state) => state.common.candidate);
  const {
    inactiveData,
    data,
    page,
    size,
    totalPages,
    status,
    totalData,
    sort,
    search,
    selectedData,
  } = useSelector((state) => state.scenario.table);

  const [params, setParams] = useState({
    page: 1,
    size: 5,
    with_archived: inactiveData,
    sort,
  });

  const handleClickArchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map(
          (scenario) =>
            !scenario.archived_at &&
            dispatch(archiveScenario({id: scenario.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        archiveScenario.fulfilled.match(result),
      );

      if (
        successfulResults.every((result) =>
          archiveScenario.fulfilled.match(result),
        )
      ) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        if (!inactiveData) {
          dispatch(setTotalData(totalData - successfulResults.length));
        }

        setParams(newParams);
        dispatch(getScenarios(newParams));
      }
    } catch (e) {
      console.log("Error archiving all scenarios: ", e);
    }
  }, [page, size, search, sort, inactiveData, totalData, selectedData]);

  const removeAllScenarios = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map((scenario) =>
          dispatch(deleteScenario({id: scenario?.id})),
        ),
      );

      if (results.every((result) => deleteScenario.fulfilled.match(result))) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(setTotalData(totalData - results.length));
        setParams(newParams);
        dispatch(getScenarios(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error removing all scenarios: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedData, totalData]);

  const handleClickUnarchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map(
          (scenario) =>
            scenario.archived_at &&
            dispatch(unArchiveScenario({id: scenario.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        unArchiveScenario.fulfilled.match(result),
      );

      if (
        successfulResults.every((result) =>
          unArchiveScenario.fulfilled.match(result),
        )
      ) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        setParams(newParams);
        dispatch(getScenarios(newParams));
      }
    } catch (e) {
      console.log("Error unarchiving all scenarios: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedData]);

  const handleClickDeleteAll = () => {
    openPopup(
      <DeleteDataPopup
        onClickNo={() => closePopup()}
        onClickYes={() => removeAllScenarios()}
      />,
      {closeButton: false},
    );
  };

  const handleCheckFilter = () => {
    dispatch(setPage(1));
    dispatch(setInactiveData(!inactiveData));
  };

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    dispatch(setTotalPages(Math.ceil(totalData / +option)));
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value.toLowerCase()));
        dispatch(getScenarios({page: 1, size, search: value}));
      }, 500);
    },
    [dispatch, size],
  );

  const handleClickArchiveScenario = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(archiveScenario({id: item.id}));

        if (archiveScenario.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          if (!inactiveData) {
            dispatch(decrementTotalData());
          }

          setParams(newParams);
          dispatch(getScenarios(newParams));
        }
      } catch (e) {
        console.log("Error archiving scenario: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const handleClickUnarchiveScenario = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(unArchiveScenario({id: item.id}));

        if (unArchiveScenario.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          setParams(newParams);
          dispatch(getScenarios(newParams));
        }
      } catch (e) {
        console.log("Error unarchiving scenario: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const removeScenario = useCallback(
    async (id) => {
      try {
        const resultAction = await dispatch(deleteScenario({id}));

        if (deleteScenario.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          dispatch(decrementTotalData());
          setParams(newParams);
          dispatch(getScenarios(newParams));
          closePopup();
        }
      } catch (e) {
        console.log("Error deleting scenario: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const handleClickDeleteScenario = async (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => removeScenario(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    {
      id: 1,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("button.deactivate")}
          onClick={() => handleClickArchiveAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((user) => !user.archived_at)
          }
        />
      ),
    },
  ];

  const advancedControls = [
    {
      id: 3,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("table.button.activate")}
          onClick={() => handleClickUnarchiveAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((scenario) => scenario.archived_at) ||
            selectedData.some((scenario) => !scenario.archived_at)
          }
        />
      ),
    },
    {
      id: 4,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("table.button.delete")}
          onClick={() => handleClickDeleteAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((scenario) => scenario.archived_at) ||
            selectedData.some((scenario) => !scenario.archived_at)
          }
        />
      ),
    },
  ];

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.name"),
        accessor: "name",
        width: 275,
      },
      {
        Header: t("table.products"),
        accessor: "products",
        Cell: ({value}) => {
          return (
            <>
              {value &&
                value.map((product, index) => (
                  <i
                    key={product.name}
                    style={
                      product.deleted || product.archived_at
                        ? {opacity: "0.5", cursor: "default"}
                        : {opacity: "1", cursor: "default"}
                    }
                    title={
                      product.deleted
                        ? t("table.deleted_product")
                        : product.archived_at
                          ? t("table.archived_product")
                          : product.name
                    }
                  >
                    {product.name}
                    {index < value.length - 1 ? ", " : ""}
                  </i>
                ))}
            </>
          );
        },
        width: 250,
      },
      {
        Header: t("table.created"),
        accessor: "created_at",
        Cell: ({value}) => (value ? moment(value).format("DD-MM-YYYY") : ""),
        width: 235,
      },
      {
        Header: t("table.due"),
        accessor: "finish_date",
        Cell: ({value}) => (value ? moment(value).format("DD-MM-YYYY") : ""),
        width: 235,
      },
      {
        Header: t("table.status"),
        accessor: "status",
        Cell: ({value}) => {
          switch (value) {
            case "failed":
              return t("table.status_failed");
            case "pending":
              return t("table.status_pending");
            case "processing":
              return t("table.status_processing");
            case "completed":
              return t("table.status_completed");
            default:
              return value;
          }
        },
        width: 200,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <>
                {!row.original.archived_at && (
                  <TableButton
                    icon={"edit"}
                    color={"var(--blueLight)"}
                    label={t("table.button.edit")}
                    action={"edit"}
                    item={row.original}
                  />
                )}

                {row.original.archived_at ? (
                  <>
                    <TableButton
                      icon={"activate"}
                      color={"var(--green)"}
                      label={t("table.button.activate")}
                      action={"activate"}
                      onClickAction={() =>
                        handleClickUnarchiveScenario(row.original)
                      }
                      item={row.original}
                      disabled={row.original.products.some(
                        (product) => product.deleted || product.archived_at,
                      )}
                      title={
                        row.original.products.some((product) => product.deleted)
                          ? t("table.deleted_some_product")
                          : row.original.products.some(
                                (product) => product.archived_at,
                              )
                            ? t("table.archived_some_product")
                            : ""
                      }
                    />

                    <TableButton
                      icon={"delete"}
                      color={"var(--red)"}
                      label={t("table.button.delete")}
                      action={"delete"}
                      onClickAction={() =>
                        handleClickDeleteScenario(row.original)
                      }
                      item={row.original}
                    />
                  </>
                ) : (
                  <TableButton
                    icon={"deactivate"}
                    color={"var(--red)"}
                    label={t("table.button.deactivate")}
                    action={"deactivate"}
                    onClickAction={() =>
                      handleClickArchiveScenario(row.original)
                    }
                    item={row.original}
                  />
                )}
              </>
            )}
          </div>
        ),
        width: 210,
      },
    ];

    if (!demo) {
      const allProductDeleted =
        data?.every((scenario) =>
          scenario.products?.some((product) => product.deleted),
        ) ?? false;

      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox
              disabled={allProductDeleted}
              {...getToggleAllRowsSelectedProps()}
            />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox
              disabled={row.original.products.some(
                (product) => product.deleted,
              )}
              {...row.getToggleRowSelectedProps()}
            />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, page, size, search, sort, inactiveData, data]);

  useEffect(() => {
    const newParams = {
      with_archived: inactiveData,
      ...(page > 0 ? {page} : {page: 1}),
      ...(size > 0 ? {size} : {size: 5}),
      ...(search && {search}),
      ...(Object.keys(sort).length !== 0 && {sort}),
    };

    setParams(newParams);
  }, [page, size, search, sort, inactiveData]);

  useDeepCompareEffect(() => {
    dispatch(getScenarios(params));
  }, [params]);

  // calculate total pages when totalData changes
  useEffect(() => {
    dispatch(setTotalPages(Math.ceil(totalData / size)));
  }, [totalData, size, dispatch]);

  // calculate page when page > totalPages and data is empty
  useEffect(() => {
    if (page === totalPages + 1) {
      dispatch(setPage(Math.ceil(totalData / size)));
    }
  }, [totalPages, totalData, size, dispatch]);

  return (
    <ScreenLayout
      title={t("page.scenarios.title")}
      controlsList={
        !demo && [...controls, ...(inactiveData ? advancedControls : [])]
      }
    >
      <TableLayout
        serverStatus={status}
        columns={columns}
        data={data}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_scenarios")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
        filterCheckbox={
          !demo && (
            <RootCheckbox
              onChange={() => handleCheckFilter()}
              label={t("page.scenarios.checkbox_filter")}
              checked={inactiveData}
            />
          )
        }
      />
    </ScreenLayout>
  );
};
